import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { SuccessToast } from 'components/success-toast';
import ErrorToast from 'components/error-toast';
import type { ServerExportStatus } from 'lib/hooks/useServerExport';

/**
 * The export statuses this toast reacts to — the `useServerExport` union plus content-export's
 * pre-202 `uploading`. A closed literal set (not a bare `string`), so a typo'd status
 * (`'completed'`, `'error'`) fails to compile at both the call site and in tests.
 */
export type ExportLifecycleStatus = ServerExportStatus['status'] | 'uploading';

/** One transient toast descriptor; `key` re-fires the self-dismissing toast on a repeat export. */
interface ToastState {
  kind: 'success' | 'error';
  message: string;
  key: number;
}

/** An override the `onComplete` hook may return to replace the default success toast. */
export interface ExportToastOverride {
  kind: 'success' | 'error';
  message: string;
}

/** The copy shown as an export moves through its lifecycle. */
export interface ExportToastMessages {
  /** Shown once the export is accepted (status → processing). */
  processing: string;
  /** Shown when the background job completes (status → complete). */
  complete: string;
}

interface ExportToastOptions {
  /** Ran on the processing transition — e.g. close the export modal. */
  onProcessing?: () => void;
  /**
   * Ran on the complete transition — e.g. trigger the download. Return an
   * {@link ExportToastOverride} to REPLACE the default success toast (e.g. a completion that
   * carries no download URL should surface an error, not "your export is ready"); return
   * nothing to keep the default success toast.
   */
  onComplete?: () => ExportToastOverride | void;
  /**
   * When true a `failed` status only toasts if it arrived FROM `processing` (a post-202,
   * background failure); a pre-202 (`uploading → failed`) failure is left to the caller's own
   * inline handling. A modal-backed flow (content-export) needs this; a modal-less one does not.
   */
  failOnlyFromProcessing?: boolean;
  /** Clear any showing toast when the export resets to `idle` — e.g. the caller reset on eval switch. */
  clearOnIdle?: boolean;
  /**
   * Shown when the export resets from `processing` straight to `idle` — i.e. an in-flight run was
   * abandoned (the caller reset on an eval switch). Surfaces the loss rather than silently wiping
   * the "started" toast. Only fires on `processing → idle`; a reset from any other status clears
   * (per `clearOnIdle`).
   */
  cancelledMessage?: string;
  /**
   * Downward pixel offset for the rendered toast, so two concurrent export toasts don't stack at
   * the same fixed coordinates. The toast components portal to `document.body`, so a parent wrapper
   * can't reposition them — the offset is applied on the toast itself. Default 0 (top slot).
   */
  stackOffset?: number;
}

/**
 * The shared success/failure toast for a server-side export, driven off the export's status
 * transitions. Extracted from `content-export` and the scenario-assumptions flow so a third export
 * action doesn't spawn a fourth copy — and page-agnostic (React + the shared toast components
 * only), so it lives in `lib/hooks` alongside its sibling `useScenarioAssumptionsExport`.
 *
 * The success toast self-dismisses after 10s and clears its own state via `onClose`; the error
 * toast stands until the viewer dismisses it (`timeout={0}`), which is right for a failure that
 * must be acknowledged. Transition-only: it fires on a change of `status`, never on an unrelated
 * re-render (non-status inputs are read from a ref at transition time).
 *
 * @param {ExportLifecycleStatus} status - The export status.
 * @param {string | null | undefined} error - The failure message, surfaced on `failed`.
 * @param {ExportToastMessages} messages - The processing/complete copy.
 * @param {ExportToastOptions} [options] - Per-flow transition hooks, the failure-guard toggle, and stacking.
 * @return {ReactNode} The toast node, or null when idle or dismissed.
 */
export function useExportLifecycleToast(
  status: ExportLifecycleStatus,
  error: string | null | undefined,
  messages: ExportToastMessages,
  options?: ExportToastOptions
): ReactNode {
  const [toast, setToast] = useState<ToastState | null>(null);

  // Latest non-status inputs, read at transition time so the effect depends on `status` alone.
  const latest = useRef({ error, messages, options });
  latest.current = { error, messages, options };

  const prevStatusRef = useRef(status);
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = status;
    if (prev === status) {
      return;
    }
    const { error: err, messages: msg, options: opts } = latest.current;
    const bump = (kind: ToastState['kind'], message: string) =>
      setToast((p) => ({ kind, message, key: (p?.key ?? 0) + 1 }));

    if (status === 'processing') {
      opts?.onProcessing?.();
      bump('success', msg.processing);
    } else if (status === 'complete') {
      // onComplete may override the default success toast (e.g. error on a missing download URL).
      const override = opts?.onComplete?.();
      if (override) {
        bump(override.kind, override.message);
      } else {
        bump('success', msg.complete);
      }
    } else if (
      status === 'failed' &&
      (!opts?.failOnlyFromProcessing || prev === 'processing')
    ) {
      bump('error', err ?? 'Export failed.');
    } else if (status === 'idle') {
      // A reset mid-run (processing → idle) abandoned an in-flight export — surface it rather than
      // silently wiping the "started" toast. Any other reset just clears.
      if (prev === 'processing' && opts?.cancelledMessage) {
        bump('error', opts.cancelledMessage);
      } else if (opts?.clearOnIdle) {
        setToast(null);
      }
    }
    // Transition-only: `status` is the sole trigger; everything else is read from `latest`.
  }, [status]);

  const dismiss = useCallback(() => {
    setToast(null);
    return true;
  }, []);

  if (!toast) {
    return null;
  }
  const stackOffset = latest.current.options?.stackOffset;
  return toast.kind === 'success' ? (
    <SuccessToast
      key={toast.key}
      message={toast.message}
      timeout={10_000}
      onClose={dismiss}
      stackOffset={stackOffset}
    />
  ) : (
    <ErrorToast
      key={toast.key}
      message={toast.message}
      timeout={0}
      onClose={dismiss}
      stackOffset={stackOffset}
    />
  );
}
